import type { ReactNode } from "react";
import { Button } from "../ui/button";
import { useControllableState } from "@erp/shared-libs/hooks";
import { PreviewObject, PreviewObjectValue } from "@erp/shared-libs/hooks";
import { Search, SlidersHorizontal, Trash2, X } from "lucide-react";

interface SearchPreviewBarProps<T> {
  /**
   * 外觀
   * - default：藥丸預覽列（既有樣式）
   * - ai：扁平 pill 預覽列（對應設計稿，圓角矩形容器 + 方角 chip + 圓角矩形查詢鈕）
   */
  variant?: "default" | "ai";
  /**
   * 隱藏 bar 前的標題
   */
  hideLabel?: boolean;
  /**
   * 隱藏查詢按鈕
   */
  hideSearchButton?: boolean;
  /**
   * 預覽值
   */
  previewValue: PreviewObject<T>;
  /**
   * 隱藏的欄位
   */
  hideColumns?: (keyof T)[];
  /**
   * 不會被清除的欄位
   * preview bar 上不會有 x 按鈕
   */
  permanentColumns?: (keyof T)[];
  /**
   * 區間（range pair）顯示端欄位
   * 這些欄位即使自身 value 為空，只要其 formatValue 回傳非空字串（通常靠 origin 讀
   * partner 欄位的值）也會顯示 chip——用於「只填上限」這類單邊區間。
   * partner 欄位請放進 hideColumns 隱藏；移除請用 onClear 一次清兩端。
   * 不在此清單的欄位維持原本「自身有值才顯示」行為。
   */
  rangeColumns?: (keyof T)[];
  /**
   * 當預覽值改變
   */
  onPreviewValueChange: (props: PreviewObject<T>) => void;
  /**
   * 清除所有的預覽值
   */
  onClearAll: () => void;
  /**
   * 查詢
   */
  onSearch: (value: { [key in keyof T]: T[key] }) => void;
  /**
   * 清除某個欄位的值
   */
  onClear?: Partial<{
    [key in keyof T]: () => void;
  }>;
  /**
   * 格式化預覽值，透過 { key: ({...}) => ReactNode } 的方式傳入
   */
  formatValue?: Partial<{
    [key in keyof T]?: ({
      columnId,
      columnName,
      value,
      origin,
    }: {
      columnId: keyof T;
      columnName: string;
      value: T[keyof T];
      origin: PreviewObject<T>;
    }) => ReactNode;
  }>;
}

function SearchPreviewBar<T>({
  variant = "default",
  hideLabel,
  hideSearchButton,
  hideColumns,
  permanentColumns,
  rangeColumns,
  onClearAll,
  onSearch,
  onClear,
  formatValue,
  ...props
}: SearchPreviewBarProps<T>) {
  // ----- hooks
  const [previewValue, setPreviewValue] = useControllableState({
    prop: props.previewValue,
    onChange: props.onPreviewValueChange,
  });

  // ----- variables
  /** 是否為 ai pill 外觀（連成一體的分段式篩選列） */
  const isAi = variant === "ai";

  // ----- functions
  /**
   * 某欄位本身是否有值（陣列看長度，其餘看非 undefined）
   */
  const hasOwnValue = (val: PreviewObjectValue<T[keyof T]>) =>
    Array.isArray(val.value) ? val.value.length > 0 : val.value !== undefined;

  /**
   * 組出單一 chip 顯示文字（有 formatValue 走自訂，陣列加總筆數、boolean 只顯示欄位名）
   * 註：rangeColumns 內的欄位亦用此結果判斷可見性，故其 formatter 需在兩端皆空時回 undefined。
   */
  const getChipLabel = (key: keyof T, val: PreviewObjectValue<T[keyof T]>) => {
    if (formatValue?.[key]) {
      return formatValue[key]!({
        columnId: key,
        columnName: val.columnName,
        value: val.value!,
        origin: previewValue,
      });
    }

    const value = val.value!;
    const text = Array.isArray(value) ? `${value.join(", ")} (共 ${value.length} 筆)` : value;

    // boolean 只顯示欄位名，false 補「：否」，true 不加額外文字
    return typeof value === "boolean"
      ? `${val.columnName}${!value ? "：否" : ""}`
      : `${val.columnName}：${text}`;
  };

  /**
   * 目前要顯示在預覽列上的篩選項目（已濾掉 hideColumns，permanent 排最前）
   * 顯示條件：
   * - 一般欄位：自身有值才顯示（原行為，不受影響）
   * - rangeColumns 內的欄位：自身有值，或其 formatValue 回傳非空字串（靠 partner 值點亮）
   */
  const visibleEntries = (
    Object.entries(previewValue) as [keyof T & string, PreviewObjectValue<T[keyof T]>][]
  )
    .filter(
      ([key, val]) =>
        (hideColumns ? !hideColumns.includes(key as keyof T) : true) &&
        (hasOwnValue(val) ||
          (!!rangeColumns?.includes(key as keyof T) && !!getChipLabel(key as keyof T, val)))
    )
    .toSorted((a) => (permanentColumns?.includes(a[0] as keyof T) ? -1 : 0));

  /**
   * 是否已有任何篩選 chip（決定空狀態提示與「清除全部」是否顯示）
   */
  const hasChips = visibleEntries.length > 0;

  /**
   * 將預覽值攤平成 { key: value } 後觸發查詢
   */
  const handleSearch = () => {
    onSearch(
      Object.fromEntries(
        Object.entries<PreviewObjectValue<T[keyof T]>>(previewValue).map(([key, val]) => [
          [key as keyof T],
          val.value,
        ])
      )
    );
  };

  /**
   * 移除單一篩選項目：有自訂 onClear 走自訂，否則把該欄位值清空
   */
  const handleRemove = (key: keyof T, val: PreviewObjectValue<T[keyof T]>) => {
    if (onClear?.[key]) {
      onClear[key]!();
      return;
    }

    setPreviewValue((prev) => ({
      ...prev,
      [key]: {
        ...val,
        value: Array.isArray(val.value) ? [] : undefined,
      },
    }));
  };

  // ----- render：ai 分段式篩選列（篩選標籤 ┆ chips ┆ 查詢，連成一條）
  if (isAi) {
    return (
      <div className="border-border-lighter bg-on-background mb-4 flex w-full items-stretch rounded-lg border">
        {/* 左段：篩選標籤 */}
        <div
          className="bg-muted-lighter text-muted-foreground-darker border-border-lighter text-dense flex h-10 shrink-0
            items-center gap-2 rounded-l-lg border-r px-4 font-semibold"
        >
          <SlidersHorizontal className="size-4" />
          <span className="hidden sm:inline">篩選條件</span>
        </div>

        {/* 中段：chips（空狀態顯示提示） */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-2.5">
          {hasChips ? (
            visibleEntries.map(([key, val]) => {
              const isPermanent = permanentColumns?.includes(key as keyof T);

              return (
                <span
                  key={key}
                  className="bg-primary-50 text-primary text-dense dark:bg-primary-300 flex shrink-0 items-center gap-1
                    rounded-md py-1 pl-2.5 pr-1 font-medium"
                >
                  {/* 篩選內容 */}
                  <span className="whitespace-nowrap">{getChipLabel(key, val)}</span>

                  {/* 移除（permanent 欄位不可移除，補一點右側留白） */}
                  {isPermanent ? (
                    <span className="w-1.5" />
                  ) : (
                    <button
                      type="button"
                      aria-label="移除此篩選"
                      onClick={() => handleRemove(key, val)}
                      className="hover:bg-primary-200/60 active:bg-primary-300/60 grid size-4 place-items-center rounded-full
                        transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </span>
              );
            })
          ) : (
            <span className="text-muted-foreground text-dense whitespace-nowrap">
              尚未設定篩選條件
            </span>
          )}
        </div>

        {/* 清除全部（有 chip 才出現） */}
        {hasChips ? (
          <Button
            type="button"
            variant="ghost"
            className="text-dense text-muted-foreground-darker border-border-lighter rounded-none border-l"
            onClick={onClearAll}
          >
            <Trash2 className="size-3.5" />
            <span className="hidden md:inline">清除</span>
          </Button>
        ) : null}

        {/* 右段：查詢（主 CTA） */}
        {hideSearchButton ? null : (
          <Button
            type="button"
            onClick={handleSearch}
            className="text-dense flex rounded-l-none rounded-r-lg transition-colors"
          >
            <Search className="size-4" />
            查詢
          </Button>
        )}
      </div>
    );
  }

  // ----- render：default 藥丸預覽列（既有樣式）
  return (
    <div className="text-foreground mb-4 flex flex-col items-center gap-2 sm:flex-row sm:items-center">
      {hideLabel ? null : <p className="hidden text-nowrap text-sm sm:block">已篩選狀態：</p>}

      <div
        className="border-border bg-on-background text-primary max-w-screen flex w-full flex-auto items-center
          overflow-hidden rounded-full border p-1.5 text-sm dark:text-white"
      >
        <div className="flex flex-1 gap-2 overflow-x-auto">
          {/* 資訊 */}
          {visibleEntries.map(([key, val]) => (
            <div
              key={key}
              className="bg-primary-50 dark:bg-primary-300 flex items-center justify-center rounded-full px-3 py-1
                font-normal"
            >
              {/* 資訊 */}
              <p className="flex-1 whitespace-nowrap text-nowrap">{getChipLabel(key, val)}</p>
              {/* 移除 x 按鈕 */}
              {permanentColumns?.includes(key as keyof T) ? null : (
                <Button
                  onClick={() => handleRemove(key, val)}
                  variant="ghost"
                  size="link"
                  className="hover:bg-primary-100/50 active:bg-primary-200/50 ml-2"
                >
                  <X />
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className="ml-auto pl-2">
          <Button onClick={onClearAll} variant="ghost" size="xs">
            <div className="flex items-center justify-center">
              <Trash2 className="" />
              <p className="ml-2 hidden text-xs md:block">清除全部</p>
            </div>
          </Button>
        </div>
      </div>

      {hideSearchButton ? null : (
        <Button
          variant="dark"
          onClick={handleSearch}
          className="w-full rounded-full px-8 sm:w-auto"
        >
          查詢
        </Button>
      )}
    </div>
  );
}

export { SearchPreviewBar };
